/**
 * Sidebar navigation configuration.
 *
 * Each section has a title and a list of items. Each item declares:
 *   - to:         route path
 *   - icon:       lucide-react icon component (name only — resolved in Layout)
 *   - label:      literal label OR i18n key (prefixed with 'i18n:')
 *   - testid:     data-testid used by tests
 *   - module:     effective_module gate (null = always visible to all users)
 *   - requires:   optional array of modules; the item shows if any is granted
 *   - managerOnly: if true, only admin / national_aftersales_manager see it
 *
 * A section is hidden if none of its items are visible for the current user.
 */

export const NAV_SECTIONS = [
  {
    title: "Comunicação",
    items: [
      {
        to: "/anuncios",
        icon: "Megaphone",
        label: "Anúncios",
        testid: "nav-announcements",
        module: null,
      },
    ],
  },
  {
    title: "Logística",
    gatingModule: "vehicle_logistics",
    items: [
      {
        to: "/logistica",
        icon: "Ship",
        label: "Viaturas",
        testid: "nav-logistics-vehicles",
        module: "vehicle_logistics",
      },
      {
        to: "/logistica/alertas",
        icon: "Bell",
        label: "Alertas",
        testid: "nav-logistics-alerts",
        module: "vehicle_logistics",
      },
      {
        to: "/logistica/configuracoes",
        icon: "Settings",
        label: "Configurações",
        testid: "nav-logistics-settings",
        module: "vehicle_logistics",
        managerOnly: true,
      },
    ],
  },
  {
    title: "Comercial",
    items: [
      {
        to: "/vehicle-registrations",
        icon: "Car",
        label: "Registo de Viaturas (VR)",
        testid: "nav-vehicle-registrations",
        module: "vehicle_registrations",
      },
      {
        to: "/vehicle-quotes",
        icon: "FileText",
        label: "Cotações de Viaturas (VRQ)",
        testid: "nav-vehicle-quotes",
        module: "vehicle_quotes",
      },
    ],
  },
  {
    title: "Operacional",
    items: [
      {
        to: "/",
        icon: "LayoutDashboard",
        label: "i18n:dashboard",
        testid: "nav-dashboard",
        module: "dashboard",
      },
      {
        to: "/mapa",
        icon: "Map",
        label: "Mapa das filiais",
        testid: "nav-branch-map",
        module: "dashboard",
      },
      {
        to: "/oficina",
        icon: "Monitor",
        label: "Modo Oficina (TV)",
        testid: "nav-workshop-board",
        module: "dashboard",
      },
      {
        to: "/service-cases",
        icon: "ClipboardList",
        label: "i18n:service_cases",
        testid: "nav-service-cases",
        module: "service_cases",
      },
      {
        to: "/body-shop-cases",
        icon: "Wrench",
        label: "Bate-chapas (BPS)",
        testid: "nav-body-shop-cases",
        module: "body_shop_cases",
      },
      {
        to: "/body-shop-cases/board",
        icon: "Hammer",
        label: "Board BPS",
        testid: "nav-bps-board",
        module: "body_shop_cases",
      },
      {
        to: "/body-shop-cases/calendar",
        icon: "CalendarRange",
        label: "Calendário BPS",
        testid: "nav-bps-calendar",
        module: "body_shop_cases",
      },
      {
        to: "/tasks",
        icon: "CheckSquare",
        label: "i18n:tasks",
        testid: "nav-tasks",
        module: "tasks",
      },
      {
        to: "/parts-requests",
        icon: "Boxes",
        label: "i18n:parts_requests",
        testid: "nav-parts-requests",
        module: "parts_requests",
      },
      {
        to: "/parts-bench-orders",
        icon: "Boxes",
        label: "Enc. Balcão (PEB)",
        testid: "nav-parts-bench-orders",
        module: "parts_bench_orders",
      },
      {
        to: "/customer-feedback",
        icon: "Users",
        label: "i18n:customer_feedback",
        testid: "nav-feedback",
        module: "customer_feedback",
      },
      {
        to: "/classification-requests",
        icon: "Wrench",
        label: "i18n:classification_requests",
        testid: "nav-classification",
        module: "classification_requests",
      },
      {
        to: "/warranty-claims",
        icon: "ShieldAlert",
        label: "i18n:warranty_claims",
        testid: "nav-warranty",
        module: "warranty_claims",
      },
      {
        to: "/maintenance-plans",
        icon: "CalendarRange",
        label: "i18n:maintenance_plans",
        testid: "nav-maintenance",
        module: "maintenance_plans",
      },
    ],
  },
  {
    title: "CRM",
    gatingModule: "crm",
    items: [
      {
        to: "/customers",
        icon: "Users",
        label: "Clientes",
        testid: "nav-crm-customers",
        module: "crm",
      },
      {
        to: "/opportunities",
        icon: "Target",
        label: "Oportunidades",
        testid: "nav-opportunities",
        module: "crm",
      },
      {
        to: "/complaints",
        icon: "AlertTriangle",
        label: "Reclamações",
        testid: "nav-complaints",
        module: "crm",
      },
    ],
  },
  {
    title: "Analítica",
    gatingModule: "reports",
    items: [
      {
        to: "/reports",
        icon: "BarChart3",
        label: "i18n:reports",
        testid: "nav-reports",
        module: "reports",
      },
      {
        to: "/satisfaction-dashboard",
        icon: "Target",
        label: "Satisfação",
        testid: "nav-satisfaction-dashboard",
        module: "reports",
      },
      {
        to: "/satisfaction-analytics",
        icon: "MessageSquare",
        label: "Análise comentários",
        testid: "nav-satisfaction-analytics",
        module: "reports",
      },
      {
        to: "/admin/flow-analytics",
        icon: "Activity",
        label: "Análise de fluxo",
        testid: "nav-flow-analytics",
        module: "reports",
        managerOnly: true,
      },
    ],
  },
  {
    title: "Configurações",
    items: [
      {
        to: "/admin",
        icon: "Settings",
        label: "i18n:admin_settings",
        testid: "nav-admin",
        module: "admin_settings",
      },
    ],
  },
];

/**
 * Resolve whether an item should be shown.
 *   can: fn(moduleKey) => boolean, from Layout.jsx closure
 *   isManager: boolean
 */
export function isItemVisible(item, { can, isManager }) {
  if (item.managerOnly && !isManager) return false;
  if (item.module == null) return true;
  return can(item.module);
}

/**
 * Filter sections against the current user permissions.
 */
export function visibleSections(ctx) {
  return NAV_SECTIONS.map((s) => ({
    ...s,
    items: s.items.filter((it) => isItemVisible(it, ctx)),
  })).filter((s) => s.items.length > 0);
}
